import { useNavigate } from "react-router-dom";
import { TextField, Button, CardMedia, Input } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { ChangeEvent, useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import { useGetProfile, useUpdateProfile } from "../../api/hooks/useAuth";
import { showSuccess, showError } from "../../helpers/toast";
import { useAuthActions } from "../../state/hooks";
import { uploadService } from "../../api/services/uploadService";
import { getQueryClient } from "../../api/queryClient";
import { queryTags } from "../../api/queryTags";

const EditProfile = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthActions();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Fetch profile data
  const { data: profileData, isLoading: isLoadingProfile, error: profileError } = useGetProfile();

  // Update form fields when profile data is loaded
  useEffect(() => {
    if (profileData?.data) {
      const profile = profileData.data;
      setFullName(profile.fullName || "");
      setEmail(profile.email || "");
      setPhone(profile.phone || "");
      setCountryCode(profile.countryCode || "+1");
      setImage(profile.image || "");
      setUser(profile);
    }
  }, [profileData, setUser]);

  // Handle profile loading error
  useEffect(() => {
    if (profileError) {
      showError(
        (profileError as any)?.response?.data?.message || "Failed to load profile"
      );
    }
  }, [profileError]);

  const { mutate: updateProfile, isPending: isUpdating } = useUpdateProfile({
    onSuccess: (response) => {
      showSuccess(response.message || "Profile updated successfully");
      if (response?.data) {
        setUser(response.data);
        // Update query cache directly with new data to ensure Topbar updates immediately
        const queryClient = getQueryClient();
        queryClient.setQueryData([queryTags.profile], response);
        // Also invalidate to trigger refetch in background
        queryClient.invalidateQueries({ queryKey: [queryTags.profile] });
      }
      navigate("/manage-profile");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update profile. Please try again.";
      showError(errorMessage);
    },
  });

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]?.type.includes("image")) {
      setFileName(files[0].name);
      setImage(URL.createObjectURL(files[0]));
      setImageFile(files[0]);
    } else {
      showError("This field only accepts images.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName || !email) {
      showError("Name and email are required");
      return;
    }

    if (fullName.trim().length < 3) {
      showError("Name must be at least 3 characters");
      return;
    }

    const numericPhone = (phone || "").replace(/\D/g, "");
    if (numericPhone && (numericPhone.length < 9 || numericPhone.length > 13)) {
      showError("Phone number must be between 9 and 13 digits");
      return;
    }

    const originalImage =
      profileData?.data?.image ||
      profileData?.data?.profilePhoto ||
      profileData?.data?.profileImage ||
      "";

    let imageUrl = image;

    // Upload image first if a new image file was selected
    if (imageFile) {
      try {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', imageFile);

        const uploadResponse = await uploadService.uploadFile(formData);
        
        // Get the S3 URL from the response
        // API response structure: { data: "https://...", message: "...", statusCode: 200 }
        // uploadResponse is ApiResponse<UploadResponse>, so uploadResponse.data is the UploadResponse
        // But the actual API returns { data: "url-string", ... }, so uploadResponse.data is the URL string
        if (uploadResponse?.data) {
          if (typeof uploadResponse.data === 'string') {
            // API returns URL directly as string in data field
            imageUrl = uploadResponse.data;
          } else if (typeof uploadResponse.data === 'object' && (uploadResponse.data as any).url) {
            // Fallback: if API returns object with url property
            imageUrl = (uploadResponse.data as any).url;
          }
        }
        
        if (imageUrl && imageUrl !== image) {
          showSuccess("Image uploaded successfully");
        }
      } catch (error: any) {
        showError(error?.response?.data?.message || "Failed to upload image");
        setUploadingImage(false);
        return;
      } finally {
        setUploadingImage(false);
      }
    }

    // Prepare payload with image URL
    const payload: any = {
      fullName,
      email,
      countryCode,
      phone: numericPhone || "",
    };

    // Add image updates: upload/change or explicit removal
    if (imageUrl && imageUrl !== originalImage) {
      payload.image = imageUrl;
    } else if (!imageUrl && originalImage) {
      // User cleared the image
      payload.image = "";
    }

    updateProfile(payload);
  };

  if (isLoadingProfile) {
    return (
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <div className="main_title">
        <h1>Edit Profile</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {' '} - <button onClick={() => navigate("/manage-profile")}>Manage Profile</button>
          {' '} - Edit Profile
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="bannerimage">Upload Image</label>
                <div className="upload_image">
                  <label
                    className="upload_image_holder"
                    htmlFor="icon-button-file"
                    style={{ cursor: isUpdating ? "not-allowed" : "pointer" }}
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg",
                      }}
                      onChange={handleImageChange}
                      disabled={isUpdating}
                    />
                    <figure>
                      <CardMedia
                        component="img"
                        image={image || "/static/images/user_placeholder.png"}
                        alt="profile"
                        sx={{ maxHeight: "200px", objectFit: "contain" }}
                      />
                    </figure>
                  </label>
                  {image && (
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setFileName("");
                        setImageFile(null);
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  )}
                </div>
              </div>
              <div className="control_group w_33">
                <label htmlFor="name">Name</label>
                <TextField
                  id="name"
                  placeholder="Enter Name"
                  hiddenLabel
                  fullWidth
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  disabled={isUpdating}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="email">Email Id</label>
                <TextField
                  id="email"
                  type="email"
                  placeholder="Enter Email Id"
                  hiddenLabel
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  country={"us"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ width: "100%" }}
                  value={`${countryCode.replace("+", "")}${phone}`}
                  onChange={(value, data: any) => {
                    const dial = data?.dialCode || "";
                    const withoutDial = value.replace(dial, "");
                    setPhone(withoutDial);
                    setCountryCode(`+${dial}`);
                  }}
                  disabled={isUpdating}
                />
              </div>
            </div>
            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary"
                variant="contained"
                disabled={isUpdating || uploadingImage}
              >
                {uploadingImage ? "Uploading image..." : isUpdating ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
